import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loader } from "@/components/Loader";
import { PaywallGate } from "@/components/PaywallGate";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { titleCase } from "@/lib/format";

export default function RBAC() {
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["rbac"],
    queryFn: async () => (await api.get("/rbac/matrix")).data,
  });

  useEffect(() => { if (data?.matrix) setMatrix(data.matrix); }, [data]);

  if (isLoading) return <Loader full />;

  const permissions = data?.permissions || [];
  const roles = data?.roles || ["doctor", "nurse", "receptionist", "pharmacist", "accountant"];

  const isOn = (role, perm) => !!matrix?.[role]?.[perm] ?? (matrix?.[role] || []).includes?.(perm);
  const toggle = (role, perm) => {
    setMatrix((m) => {
      const roleMap = { ...(m[role] || {}) };
      roleMap[perm] = !roleMap[perm];
      return { ...m, [role]: roleMap };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/rbac/matrix", { matrix });
      toast.success("Access matrix updated");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <PaywallGate>
      <PageHeader title="Roles & Access" subtitle="Control which modules each role can use" testId="page-rbac">
        <Button data-testid="rbac-save" onClick={save} disabled={saving} className="gap-1.5 bg-teal-700 hover:bg-teal-800"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
      </PageHeader>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Permission</th>
                {roles.map((r) => <th key={r} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{titleCase(r)}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {permissions.map((p) => (
                <tr key={p.key} className="hover:bg-slate-50">
                  <td className="sticky left-0 bg-white px-4 py-2.5 text-sm font-medium text-slate-700">{p.label}</td>
                  {roles.map((r) => (
                    <td key={r} className="px-4 py-2.5 text-center">
                      <Checkbox data-testid={`rbac-${r}-${p.key}`} checked={isOn(r, p.key)} onCheckedChange={() => toggle(r, p.key)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PaywallGate>
  );
}
